import { LitElement, html } from 'lit';

import 'web-dialog';
import { t } from '@lingui/macro';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import '../PostInfo';
import '../web-share';
import '../MediaCarousel';
import classNames from 'classnames';
import { formatDistanceLocaleFunc, getMediaType, NetworksCommonName } from '../../../utils';
import { getFAIcon } from '../../masonry-utils';
import {
  AnalyticsEvents,
  AnalyticsScores,
  EmbedTypes,
  networks,
  NETWORK_ICONS,
  ShareNetworks,
} from '../../../consts';
// eslint-disable-next-line
import styles from '!!lit-css-loader!postcss-loader!sass-loader!./PostModal.scss';

class PostModal extends LitElement {
  static styles = [styles];

  static get properties() {
    return {
      shareTitle: { type: String },
      post: { type: Object },
      onClose: { type: Function },
      color: { type: String },
      disableLinks: { type: Boolean },
      disableDate: { type: Boolean },
      customLink: { type: Object },
      index: { type: Number },
    };
  }

  updated() {
    const $dialog = this.shadowRoot.querySelector('web-dialog');
    if ($dialog) {
      $dialog.addEventListener('closing', () => this.onClose());
    }
  }

  handleCloseClick() {
    const $dialog = this.shadowRoot.querySelector('web-dialog');

    if ($dialog) {
      $dialog.close();
      this.onClose();
    }
  }

  handleShareClick(network) {
    giggAnalytics.logEvent(AnalyticsEvents.SHARE_POST, {
      boardId: __gigg.accountId,
      boardType: EmbedTypes.MASONRY,
      folderId: __gigg.folderId,
      postIndex: this.index,
      postId: this.post.id,
      postType: this.post.service,
      mediaType: getMediaType(this.post.media),
      isCarousel: this.post.media?.length > 1,
      shareMethod: network,
      scoreWeight: AnalyticsScores.SHARE_POST,
      firstEvent: {
        _id: window.giggFirstEvent?._id,
        createdDate: window.giggFirstEvent?.createdDate,
      },
      inViewEvent: {
        _id: window.latestViewEvent?._id,
        createdDate: window.latestViewEvent?.createdDate,
      },
    });
  }

  render() {
    if (!this.post) return null;
    const { media = [], username, time, caption, link, service, customLink, favicon } = this.post;
    const formatDistance = formatDistanceLocaleFunc();

    const renderMedia = () => {
      if (this.post.service === networks.TIK_TOK) {
        return html`
          <iframe
            src="https://www.tiktok.com/embed/v2/${this.post.id}?lang=en-US&amp;"
            style="width: 100%; height: 728px; display: block; visibility: unset; max-height: 728px;"
          ></iframe>
        `;
      }

      if (media.length) {
        return html`
          <div class="post-modal__media">
            ${media.length > 1
              ? html`<media-carousel
                  class="post-modal__media-viewer"
                  .media=${media}
                  service=${service}
                ></media-carousel>`
              : html`
                  <media-viewer
                    class="post-modal__media-viewer"
                    .media=${media[0]}
                    service=${service}
                    ?controls=${true}
                  ></media-viewer>
                `}
          </div>
        `;
      }

      return '';
    };

    return html`
      <web-dialog
        class=${classNames('post-modal', { 'post-modal--text': !media.length })}
        open=${!!this.post}
      >
        <article class="post-modal__body">
          ${renderMedia()}

          <div class="post-modal__info">
            <div class="post-modal__header">
              <div class="post-modal__postinfo">
                ${username ? html`<div class="post-modal__handle">@${username}</div>` : ''}
                ${time && !this.disableDate
                  ? html`
                      <div class="post-modal__date">
                        <img src=${favicon} alt="Favicon" />
                        ${formatDistance(new Date(time || 0), new Date())} ${t`ago`}
                      </div>
                    `
                  : ''}
              </div>
              <div
                class=${classNames(
                  'post-modal__social-icon',
                  `post-modal__social-icon--${NetworksCommonName[service]}`
                )}
              >
                ${getFAIcon(NETWORK_ICONS[NetworksCommonName[service]])}
              </div>
            </div>
            <div class="post-modal__caption">
              <post-caption caption=${caption} service=${service} color=${this.color}>
                ${customLink?.linkUrl
                  ? html`<a
                      @click=${(e) => {
                        e.stopPropagation();
                        giggAnalytics.logEvent(AnalyticsEvents.CTA_CLICK, {
                          boardId: __gigg.accountId,
                          boardType: EmbedTypes.MASONRY,
                          folderId: __gigg.folderId,
                          postIndex: this.index,
                          postId: this.post.id,
                          postType: service,
                          mediaType: getMediaType(media),
                          isCarousel: media?.length > 1,
                          CTA_URL: customLink.linkUrl,
                          scoreWeight: AnalyticsScores.CTA_CLICK,
                          firstEvent: {
                            _id: window.giggFirstEvent?._id,
                            createdDate: window.giggFirstEvent?.createdDate,
                          },
                          inViewEvent: {
                            _id: window.latestViewEvent?._id,
                            createdDate: window.latestViewEvent?.createdDate,
                          },
                        });
                      }}
                      href=${customLink?.linkUrl}
                      target="_blank"
                      class="post-modal__cta-btn"
                      >${customLink?.linkText}</a
                    >`
                  : ''}
              </post-caption>
            </div>
            <div class="post-modal__footer">
              <div class="post-modal__btns">
                <a href=${link} target="_blank" class="post-modal__btn">${t`View Post`}</a>
              </div>
              <div class="post-modal__share-links">
                <facebook-share
                  .post=${this.post}
                  @click=${() => this.handleShareClick(ShareNetworks.FACEBOOK)}
                ></facebook-share>
                <twitter-share
                  .post=${this.post}
                  @click=${() => this.handleShareClick(ShareNetworks.TWITTER)}
                ></twitter-share>
                <pinterest-share
                  .post=${this.post}
                  @click=${() => this.handleShareClick(ShareNetworks.PINTEREST)}
                ></pinterest-share>
                <linkedin-share
                  .post=${this.post}
                  @click=${() => this.handleShareClick(ShareNetworks.LINKEDIN)}
                ></linkedin-share>
                <email-share
                  .post=${this.post}
                  shareTitle=${this.shareTitle}
                  @click=${() => this.handleShareClick(ShareNetworks.EMAIL)}
                ></email-share>
                <copy-share
                  .post=${this.post}
                  .postIndex=${this.index}
                  @click=${() => this.handleShareClick(ShareNetworks.LINK)}
                ></copy-share>
              </div>
            </div>
          </div>
        </article>
        <button class="post-modal__close-btn" @click=${this.handleCloseClick}>
          ${getFAIcon(faTimes)}
        </button>
      </web-dialog>
    `;
  }
}

export default PostModal;
